/**
 * Render-time formatters for document titles.
 *
 * Meeting minutes carry an approval state (board has approved the minutes vs.
 * still draft). The flag lives on the `is_approved` field of the document
 * record. We render the title with a " (approved)" or " (unapproved)" suffix
 * so owners and admins can see the state at a glance.
 *
 * Keeping the suffix in render rather than baked into the title field means
 * the stored title stays stable across flips, and the format can change in
 * one place if the board wants different wording later.
 */

export interface DocumentForDisplay {
  title: string;
  category: string;
  is_approved?: boolean;
}

export function formatDocTitle(doc: DocumentForDisplay): string {
  if (doc.category !== 'minutes') return doc.title;
  return `${doc.title} (${doc.is_approved ? 'approved' : 'unapproved'})`;
}

const MONTHS: Record<string, number> = {
  january: 0,
  february: 1,
  march: 2,
  april: 3,
  may: 4,
  june: 5,
  july: 6,
  august: 7,
  september: 8,
  october: 9,
  november: 10,
  december: 11,
};

/**
 * Returns a millisecond timestamp parsed out of the document title so the list
 * can be sorted by what the document is ABOUT, not by when it was uploaded.
 *
 *   "Board of Directors Meeting, January 23, 2026"  -> 2026-01-23
 *   "Budget Meeting Minutes, October 29, 2025"      -> 2025-10-29
 *   "2026 Approved Budget"                          -> 2026-01-01
 *   "Structural Integrity Reserve Study, 2024"      -> 2024-01-01
 *   "Declaration of Condominium..."                 -> 0 (sorts to bottom)
 *
 * Logic: first try "Month [day,] year". If that fails, fall back to the latest
 * 4-digit 19xx/20xx year anywhere in the title.
 */
export function parseDocChronoTimestamp(title: string): number {
  const monthMatch = title.match(/([A-Z][a-z]+)\s+(?:(\d{1,2}),?\s+)?(\d{4})\b/);
  if (monthMatch) {
    const monthName = monthMatch[1];
    const dayStr = monthMatch[2];
    const yearStr = monthMatch[3];
    if (monthName && yearStr) {
      const month = MONTHS[monthName.toLowerCase()];
      if (month !== undefined) {
        const day = dayStr ? parseInt(dayStr, 10) : 1;
        return new Date(parseInt(yearStr, 10), month, day).getTime();
      }
    }
  }
  // Pick the LAST 4-digit year in the title so titles like "Amendments
  // Approved and Filed with Brevard Court, January 10, 2021" still sort to
  // 2021 even when the monthMatch above missed.
  const yearMatches = [...title.matchAll(/\b(20\d{2}|19\d{2})\b/g)];
  if (yearMatches.length > 0) {
    const last = yearMatches[yearMatches.length - 1];
    if (last && last[1]) {
      return new Date(parseInt(last[1], 10), 0, 1).getTime();
    }
  }
  return 0;
}
